import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Truck, Package, Search, RefreshCw, MapPin, Clock, CheckCircle2,
  ExternalLink, AlertCircle, Filter
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  in_transit: 'bg-amber-100 text-amber-800',
  delivered: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const STATUS_ICONS = {
  pending: Clock,
  processing: Package,
  shipped: Truck,
  in_transit: Truck,
  delivered: CheckCircle2,
  failed: AlertCircle,
};

const CARRIERS = [
  { value: 'ups', label: 'UPS', trackingUrl: 'https://www.ups.com/track?tracknum=' },
  { value: 'fedex', label: 'FedEx', trackingUrl: 'https://www.fedex.com/fedextrack/?trknbr=' },
  { value: 'usps', label: 'USPS', trackingUrl: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' },
  { value: 'other', label: 'Other', trackingUrl: '' },
];

export default function AdminShippingPage({ embedded = false }) {
  const { api } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [updateData, setUpdateData] = useState({
    status: '',
    carrier: '',
    tracking_number: '',
    notes: '',
  });
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  useEffect(() => {
    fetchShipments();
  }, [statusFilter]);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/shipping/${params}`);
      setShipments(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShipment = async () => {
    try {
      await api.patch(`/shipping/${selectedShipment.id}/`, updateData);
      toast.success('Shipment updated successfully');
      setShowUpdateDialog(false);
      fetchShipments();
    } catch (error) {
      toast.error('Failed to update shipment');
    }
  };

  const handleTrackPackage = async (shipment) => {
    if (!shipment.tracking_number || !shipment.carrier) {
      toast.error('No tracking information available');
      return;
    }

    setLoadingTracking(true);
    try {
      const response = await api.get(`/shipping/${shipment.id}/track/`);
      setTrackingInfo(response.data);
      toast.success('Tracking info retrieved');
    } catch (error) {
      // If API tracking fails, open carrier website
      const carrier = CARRIERS.find(c => c.value === shipment.carrier);
      if (carrier?.trackingUrl) {
        window.open(carrier.trackingUrl + shipment.tracking_number, '_blank');
      } else {
        toast.error('Could not retrieve tracking info');
      }
    } finally {
      setLoadingTracking(false);
    }
  };

  const openUpdateDialog = (shipment) => {
    setSelectedShipment(shipment);
    setUpdateData({
      status: shipment.status,
      carrier: shipment.carrier || '',
      tracking_number: shipment.tracking_number || '',
      notes: shipment.notes || '',
    });
    setShowUpdateDialog(true);
  };

  const filteredShipments = shipments.filter(s => {
    const matchesSearch = 
      s.learner_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.course_title?.toLowerCase().includes(search.toLowerCase()) ||
      s.tracking_number?.toLowerCase().includes(search.toLowerCase()) ||
      s.kit_name?.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const stats = {
    pending: shipments.filter(s => s.status === 'pending').length,
    processing: shipments.filter(s => s.status === 'processing').length,
    shipped: shipments.filter(s => ['shipped', 'in_transit'].includes(s.status)).length,
    delivered: shipments.filter(s => s.status === 'delivered').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Shipping Management</h1>
          <p className="text-muted-foreground">Track and manage kit shipments</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('pending')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8 text-gray-500" />
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('processing')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Package className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.processing}</p>
                <p className="text-sm text-muted-foreground">Processing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('shipped')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Truck className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{stats.shipped}</p>
                <p className="text-sm text-muted-foreground">In Transit</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('delivered')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.delivered}</p>
                <p className="text-sm text-muted-foreground">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by learner, course, or tracking #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-shipments"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchShipments}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Shipments Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kit / Course</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Shipping Address</TableHead>
                <TableHead>Carrier / Tracking</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredShipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">No shipments found</TableCell>
                </TableRow>
              ) : (
                filteredShipments.map(shipment => {
                  const StatusIcon = STATUS_ICONS[shipment.status] || Clock;
                  return (
                    <TableRow key={shipment.id} data-testid={`shipment-row-${shipment.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{shipment.kit_name || 'N/A'}</p>
                          <p className="text-sm text-muted-foreground">{shipment.course_title}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{shipment.learner_name}</p>
                          <p className="text-sm text-muted-foreground">{shipment.parent_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px]">
                          {shipment.shipping_address ? (
                            <>
                              <p className="text-sm">{shipment.shipping_address.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {shipment.shipping_address.address}, {shipment.shipping_address.city}, {shipment.shipping_address.state} {shipment.shipping_address.zip}
                              </p>
                            </>
                          ) : (
                            <span className="text-muted-foreground">Pickup</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {shipment.tracking_number ? (
                          <div>
                            <Badge variant="outline" className="mb-1">
                              {CARRIERS.find(c => c.value === shipment.carrier)?.label || shipment.carrier}
                            </Badge>
                            <p className="text-xs font-mono">{shipment.tracking_number}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Not shipped</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[shipment.status] || 'bg-gray-100'}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {shipment.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {shipment.tracking_number && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleTrackPackage(shipment)}
                              disabled={loadingTracking}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openUpdateDialog(shipment)}
                            data-testid={`update-shipment-${shipment.id}`}
                          >
                            Update
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Update Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Shipment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedShipment && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedShipment.kit_name}</p>
                <p className="text-sm text-muted-foreground">For: {selectedShipment.learner_name}</p>
              </div>
            )}
            
            <div>
              <Label>Status</Label>
              <Select 
                value={updateData.status} 
                onValueChange={(v) => setUpdateData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Carrier</Label>
              <Select 
                value={updateData.carrier} 
                onValueChange={(v) => setUpdateData(prev => ({ ...prev, carrier: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  {CARRIERS.map(carrier => (
                    <SelectItem key={carrier.value} value={carrier.value}>
                      {carrier.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tracking Number</Label>
              <Input
                value={updateData.tracking_number}
                onChange={(e) => setUpdateData(prev => ({ ...prev, tracking_number: e.target.value }))}
                placeholder="1Z999AA10123456784"
                data-testid="tracking-number-input"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={updateData.notes}
                onChange={(e) => setUpdateData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateShipment} data-testid="save-shipment-btn">
              Update Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
